import React, {
  forwardRef, useCallback, useState, useMemo, useEffect, useContext, ForwardedRef, ChangeEvent, FocusEvent, ReactElement,
} from 'react';
import Highlighter from 'react-highlight-words';
import isEqual from 'lodash/isEqual';

import getNodeText from 'ia-react-core/~/utils/components/_getNodeText';
import { ReactComponent as SearchIcon } from '../../../icons/search.svg';
import KEY_CODES from '../../../constants/KEY_CODES';
import normalizeText from '../../../utils/string/normalizeText';
import DropList from '../_DropList';
import { StyledInput, StyledIcon, AutoSuggestInputGlobalStyles } from './AutoSuggestInput.styles';
import resolveStringValue from '../../../utils/components/_resolveStringValue';
import FormColContext from '../_FormColContext';
import { TextInputProps } from '../TextInput';
import { DropListOption, DropListProps } from '../_DropList/DropList';

export interface AutoSuggestInputProps<Value> extends
  Omit<DropListProps<Value>, 'onBlur' | 'onKeyDown' | 'onValueChange' | 'ref' | 'Target'>,
  Omit<TextInputProps, 'value'> {
  /** Search to use when the autoSuggest is empty. */
  emptySearch?: string;
  /** Value to use when the autoSuggest is empty. */
  emptyValue?: string;
  /** Minimum number of caracters before the suggestions are displayed. */
  minSearchLength?: number;
  /** Function to filter the options by when the search happens. */
  filterSearch?: boolean | ((search: string, option: DropListOption<Value>) => boolean);
  /** Value to restore to the input when the autosuggest opens. */
  valueToRestore?: string;
  /** Accept any free text even if it does not match an option.. */
  acceptFreeText?: boolean;
  /** Callback triggered when value changes. */
  onValueChange?: (value: Value | string, options?: { selectedOption: DropListOption<Value> }) => void;
  /** Callback triggered when the search changes. */
  onSearch?: (search: string) => void;
  /** Callback triggered when the AutoSuggest opens or closes. */
  onOpened?: (opened: boolean) => void;
}

export interface AutoSuggestInputTargetProps<Value> extends AutoSuggestInputProps<Value> {
  searchValue: string;
  isOpened: boolean;
  setIsOpened: (isOpened: boolean) => void;
}

const AutoSuggestDefaultProps: Partial<AutoSuggestInputProps<any>> = {
  emptySearch: '',
  emptyValue: '',
  minSearchLength: 1,
  filterSearch: true,
  acceptFreeText: false,
};

/**
 * @mode developer
 */
const AutoSuggestInput = forwardRef(<Value, >({
  options, value, emptySearch, emptyValue, customContent, minSearchLength, filterSearch, valueToRestore, acceptFreeText, shouldStayOpened, onSearch, onOpened, onValueChange, ...props
}: AutoSuggestInputProps<Value>, ref: ForwardedRef<HTMLInputElement>) => {
  const { colId } = useContext(FormColContext);
  const [pendingChange, setPendingChange] = useState<Value>();
  const isControlled = useMemo(() => typeof value !== 'undefined', [value]);
  const [searchValue, setSearchValue] = useState<string>(() => {
    if (isControlled && typeof value === 'string') return value as string;
    if (isControlled) {
      const selectedOption = options.find(({ value: optionValue = emptySearch, key }) => (
        optionValue === value ||
        (typeof value === 'object' && resolveStringValue(value, key) === resolveStringValue(optionValue, key))
      ));

      if (selectedOption) {
        return selectedOption.label as string;
      }
    }

    return emptySearch;
  });
  const [focusedOption, setFocusedOption] = useState<DropListOption<Value>>(null);
  const [selectedValue, setSelectedValue] = useState<Value>(value);
  const [isListOpened, setIsListOpened] = useState(false);

  const resolvedOptions = useMemo(() => {
    if (!filterSearch) return options;
    if (searchValue.length < minSearchLength) return [];
    if (filterSearch === true) return options.filter(({ label }) => normalizeText(getNodeText(label)).includes(normalizeText(searchValue)));
    return options.filter((option) => filterSearch(searchValue, option));
  }, [filterSearch, options, searchValue, minSearchLength]);

  const highlightedOptions: DropListOption<Value>[] = useMemo(() => (
    resolvedOptions.map(({ label, ...option }) => ({
      ...option,
      textLabel: getNodeText(label),
      label: (
        <Highlighter
          highlightClassName="iA_Core_AutoSuggestInput-highlight"
          searchWords={[searchValue]}
          autoEscape
          textToHighlight={getNodeText(label)}
          sanitize={normalizeText}
        />
      ),
    }))
  ), [resolvedOptions, searchValue]);

  const openOnFocus = useMemo(() => searchValue.length >= minSearchLength, [searchValue, minSearchLength]);

  const newShouldStayOpened: DropListProps<Value>['shouldStayOpened'] = useCallback((newValue, event, { isOpened }) => {
    if (shouldStayOpened && shouldStayOpened(newValue)) return true;
    if (isOpened && 'keyCode' in event && event.keyCode === KEY_CODES.SPACE) return true;
    if (isOpened && 'keyCode' in event && event.keyCode === KEY_CODES.ENTER && !resolvedOptions.length) return true;
    if (isOpened && 'keyCode' in event && event.keyCode === KEY_CODES.ENTER && focusedOption?.onClick) return true;
    return false;
  }, [focusedOption, resolvedOptions.length, shouldStayOpened]);

  const updateSearch = useCallback((newSearch: string) => {
    const selectedOption = options.find(({ value: optionValue = emptySearch, key }) => (
      optionValue === newSearch ||
        (typeof optionValue === 'object' && newSearch === resolveStringValue(optionValue, key))
    ));

    if (selectedOption) {
      setSearchValue(getNodeText(selectedOption.label));
    } else {
      setSearchValue(newSearch);
    }

    if (onSearch) onSearch(newSearch);
  }, [emptySearch, onSearch, options]);

  const updateValue = useCallback((option: DropListOption<Value>) => {
    if (!isControlled) setSelectedValue(option?.value);
    if (isControlled) setPendingChange(option?.value);
    updateSearch((typeof option?.textLabel !== 'undefined') ? option?.textLabel : emptySearch);
    setFocusedOption(null);

    if (onValueChange) onValueChange(option?.value, { selectedOption: option });
  }, [isControlled, updateSearch, emptySearch, onValueChange]);

  const handleKeyDown: DropListProps<Value>['onKeyDown'] = useCallback((event) => {
    if (event.keyCode === KEY_CODES.ENTER) {
      event.preventDefault();

      if (isListOpened && resolvedOptions?.length) {
        if (focusedOption?.onClick) {
          const { target } = event;
          focusedOption.onClick(focusedOption.value, event, { targetRef: { current: target as HTMLElement } });
        } else {
          updateValue(focusedOption);
        }
      }
    }
  }, [isListOpened, resolvedOptions?.length, focusedOption, updateValue]);

  const handleBlur = useCallback(() => {
    setFocusedOption(null);
    if (!acceptFreeText && !selectedValue && !pendingChange) {
      updateSearch(emptySearch);
    }
  }, [acceptFreeText, emptySearch, pendingChange, selectedValue, updateSearch]);

  const handleValueChange: DropListProps<Value>['onValueChange'] = useCallback((newValue, { type, selectedOption }) => {
    if (type === 'click') {
      updateValue(selectedOption);
    } else {
      setFocusedOption(selectedOption);
    }
  }, [updateValue]);

  const handleOpened = useCallback((newIsOpened: boolean) => {
    setIsListOpened(newIsOpened);
    if (onOpened) onOpened(newIsOpened);
  }, [onOpened]);

  const hasCustomContent = useMemo(() => Boolean(customContent), [customContent]);

  const Target = useMemo(() => {
    // eslint-disable-next-line react/no-unstable-nested-components
    const TargetComponent = forwardRef<HTMLInputElement, AutoSuggestInputTargetProps<Value>>(({
      value: resultValue,
      searchValue: targetSearchValue,
      isOpened,
      setIsOpened,
      minSearchLength: min,
      targetProps,
      ...p
    }, targetRef) => {
      const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const newSearch = event.target.value;

        targetProps.updateSearch(newSearch);
        if (!targetProps.isControlled) setSelectedValue(undefined);
        if (targetProps.isControlled) setPendingChange(undefined);

        if (resultValue !== undefined && resultValue !== targetProps.emptySearch && targetProps.onValueChange) {
          targetProps.onValueChange(targetProps.emptyValue, { selectedOption: null });
        }

        if (!isOpened && newSearch.length >= min) {
          setIsOpened(true);
        } else if (isOpened && newSearch.length < min && !targetProps.hasCustomContent) {
          setIsOpened(false);
        }
      }, [targetProps, resultValue, isOpened, min, setIsOpened]);

      const handleFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
        if (event.target.value.length >= min && targetProps.hasCustomContent) {
          setIsOpened(true);
        }
      }, [min, setIsOpened, targetProps.hasCustomContent]);

      return (
        <StyledInput
          leftSign={<StyledIcon as={SearchIcon} />}
          leftSignWidth={36}
          ref={targetRef}
          onChange={handleChange}
          onFocus={handleFocus}
          isOpened={isOpened}
          {...p}
          value={targetSearchValue}
        />
      );
    });
    return TargetComponent;
  }, []);

  useEffect(() => {
    if (isControlled && value !== selectedValue) {
      setSelectedValue(value);
      setPendingChange(undefined);
      if (!isListOpened && typeof value === 'string' && (value !== emptySearch || searchValue !== emptySearch)) {
        updateSearch(value);
      }
    }
  }, [emptySearch, isControlled, isListOpened, searchValue, selectedValue, updateSearch, value]);

  useEffect(() => {
    if (highlightedOptions?.length && (!focusedOption || !highlightedOptions.find((option) => isEqual(option.value, focusedOption.value)))) {
      setFocusedOption(highlightedOptions[0]);
    }
  }, [focusedOption, highlightedOptions]);

  useEffect(() => {
    if (typeof valueToRestore !== 'undefined') {
      updateSearch(valueToRestore);
    }
  }, [updateSearch, valueToRestore]);

  const targetProps = useMemo(() => ({
    updateSearch,
    isControlled,
    emptySearch,
    onValueChange,
    hasCustomContent,
    emptyValue,
  }), [emptySearch, emptyValue, hasCustomContent, isControlled, onValueChange, updateSearch]);

  return (
    <>
      <AutoSuggestInputGlobalStyles />
      <DropList<Value, { searchValue: string; openOnFocus: boolean; minSearchLength: number }>
        aria-labelledby={colId}
        {...props}
        value={selectedValue}
        options={highlightedOptions}
        searchValue={searchValue}
        Target={Target}
        noEmptyValue
        openOnFocus={openOnFocus}
        selectionByText={false}
        homeEndNavigation={false}
        selectFirstOption
        ref={ref}
        customContent={customContent}
        minSearchLength={minSearchLength}
        targetProps={targetProps}
        targetRole="combobox"
        shouldStayOpened={newShouldStayOpened}
        onValueChange={handleValueChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onOpened={handleOpened}
      />
    </>
  );
}) as (<Value, >(props: AutoSuggestInputProps<Value>) => ReactElement<AutoSuggestInputProps<Value>>) & {
  defaultProps: typeof AutoSuggestDefaultProps;
};

AutoSuggestInput.defaultProps = AutoSuggestDefaultProps;

export default AutoSuggestInput;
